import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Image, GripVertical, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { HeroSlide } from '@natsepa/database';
import { HeroImageUpload } from '../../components/HeroImageUpload';
import { BilingualField } from '../../components/BilingualField';

export function HeroSlidesList() {
  const { data, isLoading, error } = useList<HeroSlide>({ resource: 'hero-slides' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this hero slide?')) {
      deleteOne({ resource: 'hero-slides', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading hero slides: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hero Slides</h1>
          <p className="text-sm text-gray-500 mt-1">Homepage carousel images</p>
        </div>
        <button onClick={() => create('hero-slides')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Slide
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-amber-800">
          <strong>Image Requirements:</strong> 1920x1080px (16:9), max 500KB, WebP/JPEG/PNG format
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">Order</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtitle</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((slide, index) => (
              <tr key={slide.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-gray-400">
                    <GripVertical className="w-4 h-4" />
                    <span className="font-medium">{index + 1}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {slide.image ? (
                    <img src={slide.image} alt={slide.imageAlt || slide.title} className="w-24 h-14 object-cover rounded" />
                  ) : (
                    <div className="w-24 h-14 bg-gray-100 rounded flex items-center justify-center">
                      <Image className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap"><div className="font-medium text-gray-900">{slide.title}</div></td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{slide.subtitle || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('hero-slides', slide.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(slide.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No hero slides found. Click "Add Slide" to create one.</div>}
      </div>
    </div>
  );
}

// Create Hero Slide
export function HeroSlidesCreate() {
  const navigate = useNavigate();
  const { mutate: createSlide, isPending } = useCreate();
  const [form, setForm] = useState({
    titleEn: '',
    titleId: '',
    subtitleEn: '',
    subtitleId: '',
    linkUrl: '',
    sortOrder: 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.titleId) { alert('Please fill in the title'); return; }
    createSlide(
      { resource: 'hero-slides', values: { ...form, title: form.titleId || form.titleEn } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/hero-slides/edit/${newId}` : '/hero-slides'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/hero-slides')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Hero Slide</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new carousel slide</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Slide Content</h2>

          <BilingualField
            label="Title"
            valueEn={form.titleEn}
            valueId={form.titleId}
            onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'titleEn' : 'titleId']: value })}
            required
          />

          <BilingualField
            label="Subtitle"
            valueEn={form.subtitleEn}
            valueId={form.subtitleId}
            onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'subtitleEn' : 'subtitleId']: value })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link URL (optional)</label>
            <input type="text" value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} placeholder="/rooms or https://..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
            <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-amber-900 font-semibold">Image Requirements (for next step)</p>
          <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
            <li><strong>Format:</strong> WebP recommended, JPEG or PNG accepted</li>
            <li><strong>Dimensions:</strong> 1920 × 1080 px (16:9 aspect ratio, ±5% tolerance)</li>
            <li><strong>Min size:</strong> 1280 × 720 px</li>
            <li><strong>File size:</strong> 500 KB maximum</li>
          </ul>
          <p className="text-xs text-amber-700 mt-2">After creating, you'll be redirected to the edit screen to upload the image.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/hero-slides')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue to Upload
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Hero Slide
export function HeroSlidesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<HeroSlide>({ resource: 'hero-slides', id: id! });
  const { mutate: updateSlide, isPending } = useUpdate();

  const [form, setForm] = useState({
    titleEn: '',
    titleId: '',
    subtitleEn: '',
    subtitleId: '',
    imageAltEn: '',
    imageAltId: '',
    linkUrl: '',
    sortOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    if (data?.data) {
      const slide = data.data;
      setForm({
        titleEn: (slide as any).titleEn || (slide as any).title_en || slide.title || '',
        titleId: (slide as any).titleId || (slide as any).title_id || slide.title || '',
        subtitleEn: (slide as any).subtitleEn || (slide as any).subtitle_en || slide.subtitle || '',
        subtitleId: (slide as any).subtitleId || (slide as any).subtitle_id || slide.subtitle || '',
        imageAltEn: (slide as any).imageAltEn || (slide as any).image_alt_en || slide.imageAlt || '',
        imageAltId: (slide as any).imageAltId || (slide as any).image_alt_id || slide.imageAlt || '',
        linkUrl: slide.linkUrl || '',
        sortOrder: slide.sortOrder || 0,
        isActive: true,
      });
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSlide(
      { resource: 'hero-slides', id: id!, values: form },
      { onSuccess: () => navigate('/hero-slides') }
    );
  };

  const handleImageUploaded = (url: string) => {
    console.log('Image uploaded:', url);
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/hero-slides')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Hero Slide</h1>
          <p className="text-sm text-gray-500 mt-1">Update slide content and image</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Slide Content</h2>

            <BilingualField
              label="Title"
              valueEn={form.titleEn}
              valueId={form.titleId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'titleEn' : 'titleId']: value })}
              required
            />

            <BilingualField
              label="Subtitle"
              valueEn={form.subtitleEn}
              valueId={form.subtitleId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'subtitleEn' : 'subtitleId']: value })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link URL (optional)</label>
              <input type="text" value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} placeholder="/rooms or https://..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/hero-slides')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Hero Image</h2>
            <HeroImageUpload
              slideId={id!}
              currentImage={data?.data?.image}
              onUploadComplete={handleImageUploaded}
              alt={form.imageAltId || form.imageAltEn}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Image Alt Text</h2>
            <BilingualField
              label="Alt Text"
              valueEn={form.imageAltEn}
              valueId={form.imageAltId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'imageAltEn' : 'imageAltId']: value })}
              placeholder="Deskripsi gambar..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
